import moment from 'moment';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { rgba } from 'polished';
import styled from 'styled-components';
import {
  BillableIcon,
  Button,
  ColumnsButton,
  ColumnsDrawer,
  DateTime,
  DeleteConfirmation,
  ExportDialog,
  FiltersBar,
  Icon,
  Page,
  SearchInput,
  Tag,
  Tags,
  TaskCategoryIcon,
  Tooltip,
} from '~/components';
import { FilterButton, SingleSelectFilter, TaskFiltersBar } from '~/components/filters';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle, useLocalStorage, useSearchParams, useSearchParamsConfig, useViewColumns } from '~/hooks';
import ShiftBlockedTasksConfirmation from '~/routes/app/project-tasks/ShiftBlockedTasksConfirmation';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { BillableIconContainer } from '~/components/BillableIcon';
import Assignments from '../components/Assignments';
import ExportTasksAction from '../components/ExportTasksAction';
import NewTaskAction from '../components/NewTaskAction';
import TaskFiltersDrawer from '../components/TaskFiltersDrawer';
import TasksViewNav from '../components/TasksViewNav';
import ProjectTaskDetailsDrawer from '../../../project-tasks/ProjectTaskDetailsDrawer';
import ProjectTaskEditDrawer from '../../../project-tasks/ProjectTaskEditDrawer';
import projectTaskStatuses from '~/lookups/project-task-statuses';
import buildGanttModel, { ARROW_GAP, arrowBetween, leftAnchor, rightAnchor, ROW_H } from './buildGanttModel';

const rem = (px) => `${px / 16}rem`;
const NAME_WIDTH = 15.5; // rem — default width of the sidebar name column
const NAME_MIN_PX = 160; // 10rem
const NAME_MAX_PX = 640; // 40rem
const MARKER_BAND = 20; // px
const PORT_R = 4; // connector port visible radius, px
const PORT_HIT_R = 7; // connector port hit radius — larger than the visible dot for easy grabbing
const PORT_GAP = ARROW_GAP; // connector node offset past the finish edge — matches where the arrow originates; also hitTestBar's drop tolerance
const ARROW_X_INSET = 7; // pull the delete × back from the arrowhead tip so it sits snug against the successor bar
// Auto-scroll edge threshold (px) and max speed (px/frame) while dragging a bar past the viewport.
const EDGE = 48;
const MAX_SPEED = 12;
const Styled = {
  Filters: styled(Page.Filters)`
    margin-top: 1.5rem;
  `,

  Container: styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${colors.grey10};
    border-radius: 0.3125rem;
    background: ${colors.white};
  `,

  // Pinned to the page scroll so the dates and column labels stay visible on large plans.
  HeaderRow: styled.div`
    display: flex;
    position: sticky;
    top: 0;
    z-index: 3;
    background: ${colors.white};
    border-radius: 0.3125rem 0.3125rem 0 0;
  `,

  // scrollLeft is mirrored from the body so dates track the bars; overflow keeps it a scroll
  // container for MonthSeg's sticky labels.
  TimelineHeaderViewport: styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;

    svg {
      display: block;
    }
  `,

  BodyRow: styled.div`
    display: flex;
    overflow: hidden;
    border-radius: 0 0 0.3125rem 0.3125rem;
  `,

  EmptyMessage: styled.div`
    padding: 1rem;
    color: ${colors.grey40};
    font-size: 0.875rem;
  `,

  // Holds the sidebar at full column width when there are no rows, so the empty state stays
  // aligned with the frozen header above.
  WidthSpacer: styled.div`
    display: flex;
    height: 0;
    overflow: hidden;
  `,

  Sidebar: styled.div`
    flex: none;
    width: max-content;
    position: relative;
    z-index: 1;
    border-right: 1px solid ${colors.grey10};
    transition: box-shadow 150ms ease;
    box-shadow: ${({ $scrolled }) => ($scrolled ? `0.375rem 0 0.5rem -0.375rem ${rgba(colors.black, 0.25)}` : 'none')};
  `,

  SidebarHeader: styled.div`
    display: flex;
    flex: none;
    align-items: center;
    position: relative;
    z-index: 1;
    height: ${({ $height }) => rem($height)};
    background: ${colors.grey5};
    border-right: 1px solid ${colors.grey10};
    border-bottom: 1px solid ${colors.grey10};
    font-size: 0.6875rem;
    font-weight: ${weights.black};
    letter-spacing: 0.0625rem;
    text-transform: uppercase;
    color: ${colors.grey40};
    transition: box-shadow 150ms ease;
    box-shadow: ${({ $scrolled }) => ($scrolled ? `0.375rem 0 0.5rem -0.375rem ${rgba(colors.black, 0.25)}` : 'none')};
  `,

  MarkerBand: styled.div`
    height: ${rem(MARKER_BAND)};
    border-bottom: 1px solid ${colors.grey5};
  `,

  SidebarRow: styled.div`
    display: flex;
    align-items: center;
    height: ${rem(ROW_H)};
    border-bottom: 1px solid ${colors.grey5};
    font-size: 0.8125rem;
    color: ${colors.black};
    cursor: pointer;
    transition: all 250ms ease-in-out;

    &:hover {
      z-index: 2;
      box-shadow: 0 3px 15px ${colors.grey10};
    }
  `,

  NameCell: styled.div`
    display: flex;
    align-items: center;
    width: ${({ $width }) => ($width != null ? `${$width}px` : `${NAME_WIDTH}rem`)};
    padding-right: 0.75rem;
    padding-left: ${({ $depth = 0 }) => `${0.75 + $depth}rem`};
  `,

  TodayControl: styled.div`
    align-self: center;
    display: inline-flex;
  `,

  TodayButton: styled(Button)`
    width: 1.625rem;
    height: 1.625rem;
    font-size: 0.875rem;
    color: ${colors.grey40};
    background-color: ${colors.grey5};
    border-radius: 50%;

    &:hover {
      color: ${colors.grey40};
      background-color: ${colors.grey10};
    }
  `,

  Resizer: styled.div`
    position: absolute;
    top: 0;
    right: -0.1875rem;
    width: 0.375rem;
    height: 100%;
    cursor: col-resize;
    z-index: 2;
    user-select: none;

    &:hover,
    &:active {
      background: ${rgba(colors.primary, 0.2)};
    }
  `,

  TaskName: styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,

  // Reserved width even on leaf rows so chevrons align with rollups.
  Caret: styled.span`
    flex: none;
    width: 1rem;
    margin-right: 0.25rem;
    display: inline-flex;
    justify-content: center;
    color: ${colors.grey40};
    cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

    &:hover {
      color: ${({ $interactive }) => ($interactive ? colors.grey55 : colors.grey40)};
    }
  `,

  // Same slot geometry as Caret so the chevrons align.
  HeaderToggle: styled.span`
    flex: none;
    width: 1rem;
    margin-right: 0.25rem;
    display: inline-flex;
    justify-content: center;
    color: ${colors.grey40};
    cursor: pointer;

    &:hover {
      color: ${colors.grey55};
    }
  `,

  Cell: styled.div`
    flex: none;
    width: ${({ $width }) => `${$width}rem`};
    padding: 0 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: ${({ $align }) => $align ?? 'left'};
    color: ${({ $header }) => ($header ? colors.grey40 : colors.grey75)};
  `,

  Status: styled.span`
    display: inline-flex;
    align-items: center;
  `,

  TooltipTags: styled(Tags)`
    gap: 0.25rem;

    ${Tag} {
      margin: 0;
    }

    ${BillableIconContainer} {
      width: 0.875rem;
      height: 0.875rem;
      font-size: 0.5rem;
      margin-right: 0.25rem;
    }
  `,

  AssigneeCell: styled.div`
    min-width: 0;
    width: 100%;

    ${Tags} {
      flex-wrap: nowrap;
      min-width: 0;
    }

    ${Tags} > ${Tag}:first-child {
      min-width: 0;
      overflow: hidden;
    }

    ${Tags} > ${Tag}:first-child small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    ${BillableIconContainer} {
      width: 0.875rem;
      height: 0.875rem;
      font-size: 0.5rem;
      margin-right: 0.25rem;
    }
  `,

  Dot: styled.span`
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    flex: none;
    background: ${({ $color }) => $color};
  `,

  TimelineScroll: styled.div`
    flex: 1;
    min-width: 0; /* shrink to flex space, not SVG content */
    overflow-x: auto;
    overflow-y: hidden;

    /* styled so the bar reserves layout space instead of overlaying the last row */
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: 0.5rem;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.grey25};
      border-radius: 0.25rem;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.grey55};
    }

    /* avoids the inline-SVG baseline gap */
    svg {
      display: block;
    }
  `,

  // Per-month segments let each label ride the left edge via position:sticky, pushed out by the
  // next month's boundary. Height 0 + z-index overlays the SVG header's top tier.
  MonthStrip: styled.div`
    position: relative;
    z-index: 1;
    height: 0;
    width: ${({ $width }) => `${$width}px`};
    pointer-events: none;
  `,

  // No overflow:hidden — it would make this the sticky scroll container and break the pin.
  MonthSeg: styled.div`
    position: absolute;
    top: 0;
    left: ${({ $left }) => `${$left}px`};
    width: ${({ $w }) => `${$w}px`};
    box-sizing: border-box;
    padding: 0 0.5rem;
    display: flex;
    align-items: center;
    height: ${({ $h }) => `${$h}px`};

    span {
      position: sticky;
      left: 0.5rem;
      font-size: 0.6875rem;
      color: ${colors.grey55};
      white-space: nowrap;
    }
  `,

  TooltipContainer: styled.div`
    padding: 0.75rem 0;
    display: flex;
    flex-direction: column;
  `,

  TooltipRow: styled.div`
    display: flex;
    font-size: 0.75rem;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 0.75rem;
    }
  `,

  TooltipLabel: styled.div`
    width: 7.5rem;
    padding-right: 0.5rem;
    color: ${colors.grey40};
    font-weight: ${weights.black};
    letter-spacing: 0.0625rem;
    text-transform: uppercase;
    flex-shrink: 0;
    white-space: nowrap;
  `,

  TooltipValue: styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    overflow: hidden;
  `,

  TooltipName: styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  `,

  TooltipNameIcon: styled.span`
    display: inline-flex;
    > * {
      width: auto;
      margin-right: 0.125rem;
    }
  `,

  Bar: styled.g`
    cursor: pointer;

    .gantt-link-handle .gantt-link-port {
      fill: ${colors.grey55};
      transition: fill 150ms ease;
    }

    .gantt-link-handle .gantt-link-tether {
      stroke: ${colors.grey55};
      transition: stroke 150ms ease;
    }

    .gantt-link-handle:hover .gantt-link-port {
      fill: ${colors.primary};
    }

    .gantt-link-handle:hover .gantt-link-tether {
      stroke: ${colors.primary};
    }
  `,

  DragTooltip: styled.div`
    width: max-content;
    padding: 0.375rem 0.625rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.75rem;
    border-radius: 0.3125rem;
    background: ${colors.white};
    border: solid 1px ${colors.grey10};
    box-shadow: 0 0.1875rem 1rem ${rgba(colors.black, 0.25)};
    pointer-events: none;
    z-index: 300;
  `,

  DragTooltipRow: styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    white-space: nowrap;
  `,

  // Shown only while dragging: holds one cursor across the whole drag and absorbs
  // pointer events so the bars underneath can't flip the cursor or fire hover tooltips.
  DragOverlay: styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    cursor: ${({ $cursor }) => $cursor};
  `,
};

// Hover card anchored to the cursor's horizontal position (not the bar center), mirroring
// the allocations screen. Same chrome as the shared Tooltip; rendered via a virtual Popper reference.
const HoverArrow = styled.div`
  &,
  &::before {
    position: absolute;
    width: 8px;
    height: 8px;
    background: ${colors.white};
  }

  visibility: hidden;

  &::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }
`;

const HoverBox = styled.div`
  max-width: 22rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  border-radius: 0.3125rem;
  background: ${colors.white};
  border: solid 1px ${colors.grey10};
  box-shadow: 0 0.1875rem 1rem ${rgba(colors.black, 0.25)};
  z-index: 300;

  &[data-popper-placement^='top'] > ${HoverArrow} {
    bottom: -4px;
  }

  &[data-popper-placement^='bottom'] > ${HoverArrow} {
    top: -4px;
  }
`;

function BarHoverTooltip({ task, project, referenceElement, left }) {
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);

  const virtualReference = useMemo(() => {
    const rect = referenceElement.getBoundingClientRect();
    return {
      getBoundingClientRect: () => ({
        top: rect.top,
        bottom: rect.bottom,
        height: rect.height,
        left,
        right: left,
        width: 0,
      }),
    };
  }, [referenceElement, left]);

  const { styles, attributes } = usePopper(virtualReference, popperElement, {
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'flip', options: { allowedAutoPlacements: ['top', 'bottom'] } },
      { name: 'arrow', options: { element: arrowElement } },
    ],
    placement: 'top',
    strategy: 'fixed',
  });

  return ReactDOM.createPortal(
    <HoverBox ref={setPopperElement} style={styles.popper} {...attributes.popper}>
      <BarTooltipContent task={task} project={project} />
      <HoverArrow ref={setArrowElement} style={styles.arrow} />
    </HoverBox>,
    document.body,
  );
}

// Date tooltip shown while dragging/resizing a bar. Portaled to the body via a virtual
// reference on the live bar position, mirroring BarHoverTooltip — so it isn't clipped by
// the timeline's scroll container or hidden under the sticky header.
function BarDragTooltip({ svgRef, draftBar, bar, anchorX }) {
  const [popperElement, setPopperElement] = useState(null);

  const virtualReference = useMemo(() => {
    const svgRect = svgRef.current.getBoundingClientRect();
    const top = svgRect.top + MARKER_BAND + draftBar.y;
    const centerX = svgRect.left + anchorX;
    const height = bar?.h ?? ROW_H;
    return {
      getBoundingClientRect: () => ({ top, bottom: top + height, height, left: centerX, right: centerX, width: 0 }),
    };
  }, [svgRef, draftBar.y, anchorX, bar]);

  const { styles, attributes } = usePopper(virtualReference, popperElement, {
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'flip', options: { allowedAutoPlacements: ['top', 'bottom'] } },
    ],
    placement: 'top',
    strategy: 'fixed',
  });

  return ReactDOM.createPortal(
    <Styled.DragTooltip ref={setPopperElement} style={styles.popper} {...attributes.popper}>
      <Styled.DragTooltipRow>
        <Icon icon="calendar" type="far" color={colors.primary} />
        <DateTime value={draftBar.start} />
      </Styled.DragTooltipRow>
      {!bar?.isMilestone && (
        <Styled.DragTooltipRow>
          <Icon icon="calendar" type="far" color={colors.primary} />
          <DateTime value={draftBar.end} />
        </Styled.DragTooltipRow>
      )}
    </Styled.DragTooltip>,
    document.body,
  );
}

// Labels come from the shared lookup so they stay in sync app-wide; colors are Gantt-local.
const STATUS_COLORS = {
  not_started: colors.warning,
  in_progress: colors.success,
  completed: colors.black,
};

const STATUS = Object.fromEntries(
  Object.values(projectTaskStatuses).map(({ id, name }) => [id, { label: name, color: STATUS_COLORS[id] }]),
);

const SIDEBAR_COLUMNS = {
  billability: {
    label: <BillableIcon showTooltip={false} />,
    width: 3,
    render: (task) => (task.type === 'standard' ? <BillableIcon value={task.isBillable} /> : null),
  },
  status: {
    label: 'Status',
    width: 5,
    align: 'center',
    render: (task) => {
      const status = STATUS[task.statusId];
      return (
        <Styled.Status>
          <Tooltip message={status.label}>
            <Styled.Dot $color={status.color} />
          </Tooltip>
        </Styled.Status>
      );
    },
  },
  start: {
    label: 'Start Date',
    width: 7,
    render: (task) => <DateTime value={task.start} />,
  },
  end: { label: 'End Date', width: 7, render: (task) => <DateTime value={task.end} /> },
  assignedTo: {
    label: 'Assigned To',
    width: 12,
    render: (task, project) => (
      <Styled.AssigneeCell>
        <Assignments task={task} project={project} />
      </Styled.AssigneeCell>
    ),
  },
};

function TooltipRow({ label, children }) {
  return (
    <Styled.TooltipRow>
      <Styled.TooltipLabel>{label}</Styled.TooltipLabel>
      <Styled.TooltipValue>{children}</Styled.TooltipValue>
    </Styled.TooltipRow>
  );
}

function BarTooltipContent({ task, project }) {
  const status = STATUS[task.statusId];
  const activeMembers =
    task.members?.filter((m) => m.projectMember?.isActive && m.projectMember?.member?.isActive) ?? [];
  const activeRoles = project.useRoles ? (task.roles?.filter((r) => r.role.isActive) ?? []) : [];
  const isMilestone = task.type === 'milestone';
  const durationDays = moment(task.end).diff(moment(task.start), 'days') + 1;
  const showBillable = project.isBillable && task.type === 'standard';
  const showTags = task.tags?.length > 0;
  return (
    <Styled.TooltipContainer>
      <TooltipRow label="Name">
        <Styled.TooltipNameIcon>
          <TaskCategoryIcon icon={task.taskCategory.icon} color={task.taskCategory.color} />
        </Styled.TooltipNameIcon>
        <Styled.TooltipName>{task.name}</Styled.TooltipName>
      </TooltipRow>
      <TooltipRow label="Status">
        <Styled.Dot $color={status.color} />
        {status.label}
      </TooltipRow>
      {isMilestone ? (
        <TooltipRow label="Date">
          <DateTime value={task.start} />
        </TooltipRow>
      ) : (
        <TooltipRow label="Date Range">
          <span>
            <DateTime value={task.start} /> to <DateTime value={task.end} /> ({pluralize('day', durationDays, true)})
          </span>
        </TooltipRow>
      )}
      {showBillable && (
        <TooltipRow label="Billability">
          <BillableIcon value={task.isBillable} showTooltip={false} marginRight="0" />
          {task.isBillable ? 'Billable' : 'Non-billable'}
        </TooltipRow>
      )}
      {showTags && (
        <TooltipRow label="Tags">
          <Styled.TooltipTags>
            {task.tags.map((tag) => (
              <Tag key={tag.id} style={{ backgroundColor: colors.grey5, color: colors.black }}>
                <small>{tag.name}</small>
              </Tag>
            ))}
          </Styled.TooltipTags>
        </TooltipRow>
      )}
      {(activeMembers.length > 0 || activeRoles.length > 0) && (
        <TooltipRow label="Assigned To">
          <Styled.TooltipTags>
            {activeMembers.map(({ projectMember: { member } }) => (
              <Tag key={`m-${member.id}`} style={{ backgroundColor: colors.primary10 }}>
                <small>{member.name}</small>
              </Tag>
            ))}
            {activeRoles.map(({ role }) => (
              <Tag key={`r-${role.id}`} style={{ backgroundColor: colors.grey5 }}>
                <BillableIcon value={role.isActuallyBillable} />
                <small>{role.name}</small>
              </Tag>
            ))}
          </Styled.TooltipTags>
        </TooltipRow>
      )}
    </Styled.TooltipContainer>
  );
}

// SVG text width estimated from length; shared with the flag-packing logic so both agree.
const flagWidth = (label) => label.length * 5.5 + 12;

function MarkerFlag({ x, flagX, label, color, dashed, svgHeight }) {
  const width = flagWidth(label);
  const flagY = 2;
  return (
    <g>
      <line
        x1={x}
        y1={0}
        x2={x}
        y2={svgHeight}
        stroke={color}
        strokeWidth={1.25}
        strokeDasharray={dashed ? '3 3' : undefined}
      />
      <rect x={flagX} y={flagY} width={width} height={MARKER_BAND - 4} rx={2} fill={color} />
      <text
        x={flagX + width / 2}
        y={flagY + (MARKER_BAND - 4) / 2}
        fontSize="10"
        fill={colors.white}
        textAnchor="middle"
        dominantBaseline="middle">
        {label}
      </text>
    </g>
  );
}

export default function GanttChart({ project, onViewChange, setRefetch }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();
  const documentTitle = useDocumentTitle();

  const [tasks, setTasks] = useState(null);
  const [draftBar, setDraftBar] = useState(null);
  const [draftLink, setDraftLink] = useState(null);
  const [selectedArrowId, setSelectedArrowId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragCursor, setDragCursor] = useState('ew-resize');
  const suppressClickRef = useRef(false);
  const [hover, setHover] = useState(null);
  const hoverTimer = useRef(null);
  const [dialog, setDialog] = useState(null);
  const [expandedMap, setExpandedMap] = useState(() => new Map());

  const unitStorage = useLocalStorage({ key: 'project_dashboard_tasks_gantt_unit' });
  const [unit, setUnit] = useState(() => {
    const stored = unitStorage.get();
    return ['day', 'week', 'month'].includes(stored) ? stored : 'week';
  });
  const handleUnitChange = (value) => {
    setUnit(value);
    unitStorage.set(value);
  };

  const nameWidthStorage = useLocalStorage({ key: 'project_dashboard_tasks_gantt_name_width' });
  const [nameWidth, setNameWidth] = useState(() => {
    const stored = Number(nameWidthStorage.get());
    return stored >= NAME_MIN_PX && stored <= NAME_MAX_PX ? stored : NAME_WIDTH * 16;
  });
  const [resizing, setResizing] = useState(false);
  const handleResizeDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = nameWidth;
    let latest = startWidth;
    setResizing(true);
    const handleMove = (moveEvent) => {
      latest = Math.min(NAME_MAX_PX, Math.max(NAME_MIN_PX, startWidth + (moveEvent.clientX - startX)));
      setNameWidth(latest);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      setResizing(false);
      nameWidthStorage.set(String(latest));
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  };
  const handleResizeReset = () => {
    const value = NAME_WIDTH * 16;
    setNameWidth(value);
    nameWidthStorage.set(String(value));
  };
  const [params, setParams] = useState({
    q: '',
    projectTaskRecordStatusId: '',
    projectTaskStatuses: [],
    projectTaskTags: [],
  });

  const searchParamsConfig = useSearchParamsConfig();
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        projectTaskStatuses: searchParamsConfig.projectTaskStatuses,
        projectTaskRecordStatusId: { default: 'active', ...searchParamsConfig.projectTaskRecordStatusId },
        projectTaskTags: searchParamsConfig.projectTaskTags,
      }),
      [searchParamsConfig],
    ),
    sessionKey: 'project_dashboard_tasks_gantt',
    onChange: useCallback((next) => setParams((state) => ({ ...state, ...next })), []),
  });
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((next) => {
      if (next) {
        setParams((state) => ({ ...state, ...next }));
        setSearchParamsStatus('ready');
      }
    });
  }, [searchParams, searchParamsStatus]);

  const urlSearchParams = useCallback(
    (p) => ({
      projectId: project.id,
      q: p.q,
      projectTaskStatusId: p.projectTaskStatuses?.map((v) => v.id),
      projectTaskRecordStatusId: p.projectTaskRecordStatusId ?? undefined,
      projectTaskTagId: p.projectTaskTags?.map((v) => v.id),
    }),
    [project.id],
  );

  const fetchData = useCallback(
    async (p = params) => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projects(project.id)
          .tasks()
          .list.get(urlSearchParams(p));
        setTasks(data);
        return data;
      } catch ({ message }) {
        setTasks([]);
        toast.error(message);
      }
    },
    [api, workspace.id, project.id, params, urlSearchParams, toast],
  );

  useEffect(() => {
    if (searchParamsStatus !== 'ready' || tasks) return;
    fetchData();
  }, [fetchData, searchParamsStatus, tasks]);

  useEffect(() => {
    if (setRefetch) {
      setRefetch(() => fetchData);
    }
  }, [setRefetch, fetchData]);

  const handleFilter = async (filter) => {
    const next = { ...params, ...filter };
    setParams(next);
    searchParams.set(next);
    await fetchData(next);
  };

  const handleShowFiltersDrawer = () => {
    setDialog(<TaskFiltersDrawer values={params} onApply={handleFilter} onClose={() => setDialog(null)} />);
  };

  const columns = useViewColumns({
    storageKey: 'project_dashboard_tasks_gantt_columns',
    columns: [
      { key: 'name', name: 'Name', disabled: true },
      ...(project.isBillable ? [{ key: 'billability', name: 'Billability' }] : []),
      { key: 'status', name: 'Status', default: true },
      { key: 'start', name: 'Start Date', default: true },
      { key: 'end', name: 'End Date', default: true },
      { key: 'assignedTo', name: 'Assigned To' },
    ],
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .projects(project.id)
          .tasks()
          .getExport(urlSearchParams(params), { headers: { accept: mimeType }, responseType: 'blob' })}
        onClose={resolve}
      />
    ));
  };

  const handleCloseDialog = () => {
    documentTitle.set(project.name);
    setDialog(null);
  };

  const openTask = (task) => {
    setDialog(
      task.permissions?.manage ? (
        <ProjectTaskEditDrawer
          taskId={task.id}
          project={project}
          onClose={handleCloseDialog}
          onSaved={async () => {
            await fetchData();
            handleCloseDialog();
          }}
          onDelete={async () => {
            const confirmMessage =
              project.useDependencies && (task.predecessors?.length > 0 || task.successors?.length > 0)
                ? 'Are you sure you want to delete this task? Any task dependencies will also be removed.'
                : 'Are you sure you want to delete this task?';

            const confirm = await confirmation.prompt((resolve) => (
              <DeleteConfirmation resolve={resolve}>{confirmMessage}</DeleteConfirmation>
            ));
            if (!confirm) {
              return;
            }

            try {
              await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).delete();
              await fetchData();
              handleCloseDialog();
            } catch ({ message }) {
              toast.error(message);
            }
          }}
        />
      ) : (
        <ProjectTaskDetailsDrawer taskId={task.id} project={project} onClose={handleCloseDialog} />
      ),
    );
  };

  const taskById = useMemo(() => new Map((tasks ?? []).map((t) => [t.id, t])), [tasks]);

  const commitDates = async (task, start, end) => {
    let shiftSuccessors = true;
    const endChanged = task.end !== end;
    if (project.useDependencies && endChanged) {
      try {
        // A drag never edits dependencies, so the task's current successors are the set to preview.
        const { data: blockedTasks } = await api.www
          .workspaces(workspace.id)
          .projects(project.id)
          .tasks(task.id)
          .scheduledSuccessors({ successorIds: (task.successors ?? []).map((s) => s.id) });

        if (blockedTasks.length > 0) {
          const result = await confirmation.prompt((resolve) => (
            <ShiftBlockedTasksConfirmation taskName={task.name} blockedTasks={blockedTasks} resolve={resolve} />
          ));

          if (!result) return;
          shiftSuccessors = result.shiftSuccessors;
        }
      } catch ({ message }) {
        toast.error(message);
        return;
      }
    }

    // Optimistic update so the bar holds its new position; the refetch reconciles.
    setTasks((prev) => prev?.map((t) => (t.id === task.id ? { ...t, start, end } : t)));

    try {
      await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks(task.id)
        .upsert({ name: task.name, start, end, shiftSuccessors });
    } catch ({ message }) {
      toast.error(message);
    } finally {
      await fetchData();
    }
  };

  const handleBarEnter = (e, task) => {
    if (dragging || draftLink) return;
    const ref = e.currentTarget;
    const left = e.clientX;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHover({ task, ref, left }), 300);
  };

  const endHover = () => {
    clearTimeout(hoverTimer.current);
    setHover(null);
  };

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  useEffect(() => {
    if (!selectedArrowId) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedArrowId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedArrowId]);

  const handleResizeStart = (e, bar, edge) => {
    e.stopPropagation();
    e.preventDefault();
    endHover();
    setDragging(true);
    setDragCursor('ew-resize');
    const { dayWidth } = model;
    const task = taskById.get(bar.taskId);
    const origX = bar.x;
    const origW = bar.w;
    const rightEdge = origX + origW;
    const startX = clientToContent(e.clientX, 0).x;
    let current = { start: task.start, end: task.end };

    // Resolve the draft from a pointer x in content space (so it accounts for scroll); reused on each
    // auto-scroll frame where no pointer event fires.
    const apply = (clientX) => {
      const deltaDays = Math.round((clientToContent(clientX, 0).x - startX) / dayWidth);
      let x = origX;
      let w = origW;
      if (edge === 'end') {
        w = Math.max(dayWidth, origW + deltaDays * dayWidth);
      } else {
        x = Math.min(origX + deltaDays * dayWidth, rightEdge - dayWidth);
        w = rightEdge - x;
      }
      const startDelta = Math.round((x - origX) / dayWidth);
      const endDelta = Math.round((x + w - rightEdge) / dayWidth);
      current = {
        start: moment(task.start).add(startDelta, 'days').format('YYYY-MM-DD'),
        end: moment(task.end).add(endDelta, 'days').format('YYYY-MM-DD'),
      };
      setDraftBar({ taskId: bar.taskId, anchor: edge, y: bar.y, start: current.start, end: current.end });
    };
    dragApplyRef.current = apply;

    const onMove = (ev) => {
      pointerRef.current = ev.clientX;
      apply(ev.clientX);
      updateAutoScroll(ev.clientX);
    };

    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      stopAutoScroll();
      pointerRef.current = null;
      dragApplyRef.current = null;
      setDragging(false);
      // Hold the dragged position until commitDates resolves so the bar doesn't snap back
      // behind the cascade-confirmation modal; the refetch (or reject) reconciles afterward.
      try {
        if (current.start !== task.start || current.end !== task.end) {
          await commitDates(task, current.start, current.end);
        }
      } finally {
        setDraftBar(null);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Drag the whole bar to reschedule. Distinguishes a click (opens the drawer) from a move
  // via a small threshold; once crossed, the click is suppressed and the move commits.
  const handleMoveStart = (e, bar) => {
    e.preventDefault();
    endHover();
    const { dayWidth } = model;
    const task = taskById.get(bar.taskId);
    const startClientX = e.clientX; // screen space, only for the click-vs-drag threshold
    const startX = clientToContent(e.clientX, 0).x;
    let moved = false;
    let current = { start: task.start, end: task.end };

    // Resolve the draft from a pointer x in content space (so it accounts for scroll); reused on each
    // auto-scroll frame where no pointer event fires.
    const apply = (clientX) => {
      if (!moved && Math.abs(clientX - startClientX) < 3) return;
      if (!moved) {
        moved = true;
        suppressClickRef.current = true;
        setDragging(true);
        setDragCursor('grabbing');
      }
      const deltaDays = Math.round((clientToContent(clientX, 0).x - startX) / dayWidth);
      current = {
        start: moment(task.start).add(deltaDays, 'days').format('YYYY-MM-DD'),
        end: moment(task.end).add(deltaDays, 'days').format('YYYY-MM-DD'),
      };
      setDraftBar({ taskId: bar.taskId, anchor: 'center', y: bar.y, start: current.start, end: current.end });
    };
    dragApplyRef.current = apply;

    const onMove = (ev) => {
      pointerRef.current = ev.clientX;
      apply(ev.clientX);
      // Only auto-scroll once a drag is actually underway, not on a near-edge click.
      if (moved) updateAutoScroll(ev.clientX);
    };

    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      stopAutoScroll();
      pointerRef.current = null;
      dragApplyRef.current = null;
      if (!moved) return;
      setDragging(false);
      // Hold the dragged position until commitDates resolves so the bar doesn't snap back
      // behind the cascade-confirmation modal; the refetch (or reject) reconciles afterward.
      try {
        if (current.start !== task.start || current.end !== task.end) {
          await commitDates(task, current.start, current.end);
        }
      } finally {
        setDraftBar(null);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const svgRef = useRef(null);

  // Map a viewport point into the timeline's coordinate space (inside the bars/arrows <g>,
  // which is translated down by the marker band). The svg rect already reflects scroll.
  const clientToContent = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top - MARKER_BAND };
  };

  // Resolve the bar under a timeline point: row from the y band, then x within the bar extent
  // (widened to the diamond half-width for milestones). Dateless rows have no bar.
  const hitTestBar = (gx, gy) => {
    if (gy < 0) return null;
    const row = model.rows[Math.floor(gy / ROW_H)];
    const bar = row && barById.get(row.task.id);
    if (!bar) return null;
    const pad = bar.isMilestone ? bar.mHit : PORT_GAP;
    if (gx < bar.x - pad || gx > bar.x + bar.w + pad) return null;
    return bar.taskId;
  };

  // A link is always drawn from the predecessor's finish, so the source is the predecessor and the
  // drop target is the successor.
  const linkValidity = (sourceTask, targetId) => {
    if (!targetId || targetId === sourceTask.id) return false;
    if (barById.get(targetId)?.isRollup) return false;
    const successor = taskById.get(targetId);
    if (!successor) return false;
    return !(successor.predecessors ?? []).some((p) => p.id === sourceTask.id);
  };

  // Persist a task's full predecessor set. upsertGraph replaces the relation it receives, so always
  // send the complete list. Unprompted, matching the drawer's link editing.
  const patchPredecessors = async (successorTask, predecessors) => {
    try {
      await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks(successorTask.id)
        .upsert({ name: successorTask.name, predecessors });
    } catch ({ message }) {
      toast.error(message);
    } finally {
      await fetchData();
    }
  };

  // Add the drawn edge to the successor's predecessor set.
  const commitLink = (predecessorTask, successorTask) =>
    patchPredecessors(successorTask, [
      ...(successorTask.predecessors ?? []).map((p) => ({ predecessorId: p.id })),
      { predecessorId: predecessorTask.id },
    ]);

  // Remove a dependency from the successor's predecessor set.
  const deleteLink = async (arrow) => {
    const successor = taskById.get(arrow.successorId);
    if (!successor) return;

    const confirmed = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to remove this task dependency?</DeleteConfirmation>
    ));
    if (!confirmed) return;

    setSelectedArrowId(null);
    return patchPredecessors(
      successor,
      (successor.predecessors ?? []).filter((p) => p.id !== arrow.predecessorId).map((p) => ({ predecessorId: p.id })),
    );
  };

  const handleLinkStart = (e, bar) => {
    e.stopPropagation();
    e.preventDefault();
    endHover();
    const sourceTask = taskById.get(bar.taskId);
    const x1 = rightAnchor(bar) + PORT_GAP; // the rubber-band starts at the connector node
    const y1 = bar.y + bar.h / 2;
    let drop = { targetId: null, valid: false };

    const onMove = (ev) => {
      const { x: gx, y: gy } = clientToContent(ev.clientX, ev.clientY);
      const targetId = hitTestBar(gx, gy);
      const valid = linkValidity(sourceTask, targetId);
      drop = { targetId, valid };
      setDraftLink({ sourceId: bar.taskId, x1, y1, x2: gx, y2: gy, targetId, valid });
    };

    const cleanup = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
      setDraftLink(null);
    };

    const onUp = async () => {
      cleanup();
      if (!drop.targetId || !drop.valid) return;
      await commitLink(sourceTask, taskById.get(drop.targetId));
    };

    const onKey = (ev) => {
      if (ev.key === 'Escape') cleanup();
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('keydown', onKey);
  };

  const scrollRef = useRef(null);
  const headerScrollRef = useRef(null);
  const didCenter = useRef(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  // Last pointer x, so the auto-scroll loop can re-resolve the drag on frames with no pointer event.
  const pointerRef = useRef(null);
  const scrollSpeedRef = useRef(0);
  const scrollRafRef = useRef(null);
  const dragApplyRef = useRef(null);

  const handleTimelineScroll = (e) => {
    const { scrollLeft } = e.currentTarget;
    setScrolled(scrollLeft > 0);
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = scrollLeft;
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewportWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tasks]);

  const stopAutoScroll = useCallback(() => {
    scrollSpeedRef.current = 0;
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  // Scroll one horizontal step in the drag direction, clamped to the timeline, then re-resolve the drag
  // so it keeps following the (possibly still) cursor as the content moves under it.
  const autoScrollTick = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !scrollSpeedRef.current || pointerRef.current == null) {
      scrollRafRef.current = null;
      return;
    }

    // Step in the drag direction only, clamped to the scroll range — never reverse or jump.
    const speed = scrollSpeedRef.current;
    const upper = el.scrollWidth - el.clientWidth;
    const current = el.scrollLeft;
    const next = speed > 0 ? Math.min(current + speed, upper) : Math.max(current + speed, 0);
    if (next === current) {
      stopAutoScroll();
      return;
    }

    el.scrollLeft = next;
    dragApplyRef.current?.(pointerRef.current);
    scrollRafRef.current = requestAnimationFrame(autoScrollTick);
  }, [stopAutoScroll]);

  // Set scroll speed from the pointer's distance to the left/right edge (quadratic ease-in).
  const updateAutoScroll = useCallback(
    (clientX) => {
      const el = scrollRef.current;
      if (!el) {
        stopAutoScroll();
        return;
      }

      const rect = el.getBoundingClientRect();
      let speed = 0;
      if (clientX < rect.left + EDGE) {
        const ratio = Math.min(1, (rect.left + EDGE - clientX) / EDGE);
        speed = -Math.ceil(MAX_SPEED * ratio * ratio);
      } else if (clientX > rect.right - EDGE) {
        const ratio = Math.min(1, (clientX - (rect.right - EDGE)) / EDGE);
        speed = Math.ceil(MAX_SPEED * ratio * ratio);
      }

      scrollSpeedRef.current = speed;

      if (speed && !scrollRafRef.current) scrollRafRef.current = requestAnimationFrame(autoScrollTick);
      if (!speed) stopAutoScroll();
    },
    [autoScrollTick, stopAutoScroll],
  );

  // Cancel any in-flight auto-scroll if the chart unmounts mid-drag.
  useEffect(() => () => stopAutoScroll(), [stopAutoScroll]);

  // Default to expanded.
  const getExpanded = useCallback((id) => expandedMap.get(id) ?? true, [expandedMap]);
  const setExpanded = useCallback((id, value) => {
    setExpandedMap((prev) => new Map(prev).set(id, value));
  }, []);
  const expandAll = useCallback(() => setExpandedMap(new Map((tasks ?? []).map((t) => [t.id, true]))), [tasks]);
  const collapseAll = useCallback(() => setExpandedMap(new Map((tasks ?? []).map((t) => [t.id, false]))), [tasks]);

  const model = useMemo(
    () =>
      buildGanttModel({
        tasks: tasks ?? [],
        unit,
        minWidth: viewportWidth,
        isExpanded: getExpanded,
        projectStart: project.start,
        projectEnd: project.end,
      }),
    [tasks, unit, viewportWidth, getExpanded, project.start, project.end],
  );

  const anyCollapsed = useMemo(() => model.rows.some((r) => r.hasChildren && !r.expanded), [model.rows]);

  const barById = useMemo(() => new Map(model.bars.map((b) => [b.taskId, b])), [model.bars]);

  // Derive the dragged bar's pixel bounds from the current model rather than caching them at drag
  // time: the optimistic update and refetch can shift the timeline origin, and mapping the draft's
  // dates through the live xFor keeps it aligned with the rescaled chart instead of flashing.
  const draftBounds = draftBar
    ? { x: model.xFor(draftBar.start), w: model.barWidth(draftBar.start, draftBar.end) }
    : null;
  const draftAnchorX = draftBounds
    ? draftBar.anchor === 'end'
      ? draftBounds.x + draftBounds.w
      : draftBar.anchor === 'start'
        ? draftBounds.x
        : draftBounds.x + draftBounds.w / 2
    : null;

  // Bar with the live drag bounds blended in, so arrows track the dragged bar instead of its stale position.
  const liveBar = (id) =>
    draftBar?.taskId === id && draftBounds ? { ...barById.get(id), ...draftBounds } : barById.get(id);

  // A zoom change rescales the x-axis, so re-focus is warranted.
  useLayoutEffect(() => {
    didCenter.current = false;
  }, [unit]);

  // Center on today once per focus event (initial load / zoom). Layout effect so the scroll
  // position is set before paint — avoids a flash at the left edge. Guarded so expand/collapse,
  // scrollbar remeasures, and refetches don't yank the user back to today.
  useLayoutEffect(() => {
    if (!tasks || didCenter.current || !viewportWidth) return;
    const el = scrollRef.current;
    if (!el) return;
    const target = model.todayX != null ? model.todayX - el.clientWidth / 2 : model.startX - 24;
    el.scrollLeft = Math.max(0, target);
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = el.scrollLeft;
    didCenter.current = true;
  }, [tasks, viewportWidth, model.startX, model.todayX]);

  const scrollToToday = useCallback(() => {
    const el = scrollRef.current;
    if (!el || model.todayX == null) return;
    el.scrollTo({ left: Math.max(0, model.todayX - el.clientWidth / 2), behavior: 'smooth' });
  }, [model.todayX]);

  const visibleColumns = useMemo(
    () => Object.keys(SIDEBAR_COLUMNS).filter((key) => columns.visible[key]),
    [columns.visible],
  );

  if (!tasks) return <PageLoader />;

  const headerHeight = model.headerHeight;
  // Reserves room for the empty-state message so grid lines still reach the bottom.
  const bodyHeight = tasks.length ? model.height : 56;

  // Marker label flags stay on one row, ordered left-to-right by their line position (chronological)
  // so Start never crosses past End. When two would overlap, the later one is nudged right so the
  // boxes never collide (each flag's color ties it back to its line).
  const markers = [
    { x: model.projectStartX, label: 'Start', color: colors.success, side: 'left', dashed: false },
    { x: model.todayX, label: 'Today', color: colors.grey75, side: 'right', dashed: true },
    { x: model.projectEndX, label: 'End', color: colors.danger, side: 'right', dashed: false },
  ]
    .filter((m) => m.x != null)
    .map((m) => {
      const width = flagWidth(m.label);
      return { ...m, width, naturalLeft: m.side === 'left' ? m.x - width : m.x };
    })
    .sort((a, b) => a.x - b.x);

  let flagCursor = -Infinity;
  for (const m of markers) {
    m.flagX = Math.max(m.naturalLeft, flagCursor);
    flagCursor = m.flagX + m.width;
  }

  const bodySvgHeight = MARKER_BAND + bodyHeight;

  // Live preview while drawing a dependency: snaps to the real elbow over a valid target,
  // otherwise trails the cursor. Color signals loose / valid / invalid.
  let draftLinkPath = null;
  let draftLinkStroke = null;
  let draftLinkMarker = 'gantt-arrow';
  if (draftLink) {
    draftLinkStroke = draftLink.targetId ? (draftLink.valid ? colors.primary : colors.danger) : colors.grey55;
    draftLinkMarker = draftLink.targetId
      ? draftLink.valid
        ? 'gantt-arrow-valid'
        : 'gantt-arrow-invalid'
      : 'gantt-arrow';
    draftLinkPath = `M ${draftLink.x1} ${draftLink.y1} L ${draftLink.x2} ${draftLink.y2}`;
    if (draftLink.targetId && draftLink.valid) {
      const sourceBar = barById.get(draftLink.sourceId);
      const targetBar = barById.get(draftLink.targetId);
      if (sourceBar && targetBar) {
        draftLinkPath = arrowBetween(sourceBar, targetBar);
      }
    }
  }

  // The selected dependency and its delete control live in a foreground layer (drawn after the bars)
  // so they sit above every other arrow — converging predecessor lines can't paint over the control.
  const selectedArrow = selectedArrowId ? model.arrows.find((a) => a.id === selectedArrowId) : null;
  const canDeleteSelected = selectedArrow && taskById.get(selectedArrow.successorId)?.permissions?.manage;
  // Highlight uses the same geometry as the grey original (identical trajectory), trimmed at the start
  // by the dot radius so the teal begins where the connector dot ends.
  const selectedFromBar = canDeleteSelected ? liveBar(selectedArrow.predecessorId) : null;
  const selectedToBar = canDeleteSelected ? liveBar(selectedArrow.successorId) : null;
  const selectedArrowD = selectedFromBar && selectedToBar ? arrowBetween(selectedFromBar, selectedToBar, PORT_R) : null;
  const deleteBtn = selectedArrowD
    ? { x: leftAnchor(selectedToBar) - ARROW_X_INSET, y: selectedToBar.y + selectedToBar.h / 2 }
    : null;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Tasks</Page.Title>

        <TasksViewNav value="gantt" onChange={(view) => onViewChange(view)} />

        <Page.Actions>
          <ExportTasksAction projectKey={project.key} onExport={handleExport} />
          <ColumnsButton onClick={handleShowColumnsDrawer} />
          <FilterButton onClick={handleShowFiltersDrawer} />
          <NewTaskAction
            project={project}
            onSaved={async (data, action) => {
              await fetchData();
              if (action === 'save') {
                openTask(data);
              }
            }}
          />
        </Page.Actions>
      </Page.TabHeader>

      <Styled.Filters>
        <FiltersBar>
          <SearchInput
            value={params.q}
            placeholder="Search"
            onChange={({ target: { value } }) => handleFilter({ q: value })}
          />

          <SingleSelectFilter
            icon="gear"
            placeholder="Unit"
            clearable={false}
            value={unit}
            options={[
              { id: 'month', name: 'Month' },
              { id: 'week', name: 'Week' },
              { id: 'day', name: 'Day' },
            ]}
            renderValue={(value) => value.name}
            onChange={({ value }) => handleUnitChange(value)}
          />

          <TaskFiltersBar
            filters={params}
            sticky={['projectTaskStatuses', 'projectTaskRecordStatusId', 'projectTaskTags']}
            onChange={handleFilter}
          />
        </FiltersBar>

        <Tooltip
          message={model.todayX == null ? "Today is outside this project's timeline" : 'Today'}
          Container={Styled.TodayControl}>
          <Styled.TodayButton onClick={scrollToToday} disabled={model.todayX == null} isAnchor>
            <Icon icon="calendar-day" type="far" />
          </Styled.TodayButton>
        </Tooltip>
      </Styled.Filters>

      <Page.Section sticky>
        <Styled.Container>
          <Styled.HeaderRow>
            <Styled.SidebarHeader $height={headerHeight} $scrolled={scrolled}>
              <Styled.NameCell $width={nameWidth}>
                <Styled.HeaderToggle onClick={anyCollapsed ? expandAll : collapseAll}>
                  <Tooltip message={anyCollapsed ? 'Expand All' : 'Collapse All'} delay={500}>
                    <Icon icon={anyCollapsed ? 'angle-double-down' : 'angle-double-up'} />
                  </Tooltip>
                </Styled.HeaderToggle>
                Name
              </Styled.NameCell>
              {visibleColumns.map((key) => (
                <Styled.Cell key={key} $header $width={SIDEBAR_COLUMNS[key].width} $align={SIDEBAR_COLUMNS[key].align}>
                  {SIDEBAR_COLUMNS[key].label}
                </Styled.Cell>
              ))}
            </Styled.SidebarHeader>

            <Styled.TimelineHeaderViewport ref={headerScrollRef}>
              <Styled.MonthStrip $width={model.width}>
                {model.majorTicks.map((tick, i) => {
                  const next = model.majorTicks[i + 1];
                  return (
                    <Styled.MonthSeg
                      key={`majl-${i}`}
                      $left={tick.x}
                      $w={(next ? next.x : model.width) - tick.x}
                      $h={headerHeight / 2}>
                      <span>{tick.label}</span>
                    </Styled.MonthSeg>
                  );
                })}
              </Styled.MonthStrip>
              <svg width={model.width} height={headerHeight}>
                {model.majorTicks.map((tick, i) => (
                  <line key={`maj-${i}`} x1={tick.x} y1={0} x2={tick.x} y2={headerHeight / 2} stroke={colors.grey10} />
                ))}
                {model.minorTicks.map((tick, i) => (
                  <line
                    key={`min-${i}`}
                    x1={tick.x}
                    y1={headerHeight / 2}
                    x2={tick.x}
                    y2={headerHeight}
                    stroke={colors.grey5}
                  />
                ))}
                {model.minorTicks.map((tick, i) => (
                  <text
                    key={`minl-${i}`}
                    x={tick.x + 8}
                    y={(headerHeight * 3) / 4}
                    fontSize="11"
                    fill={colors.grey55}
                    dominantBaseline="middle">
                    {tick.label}
                  </text>
                ))}
                <line x1={0} y1={headerHeight / 2} x2={model.width} y2={headerHeight / 2} stroke={colors.grey5} />
                <line x1={0} y1={headerHeight} x2={model.width} y2={headerHeight} stroke={colors.grey10} />
              </svg>
            </Styled.TimelineHeaderViewport>
          </Styled.HeaderRow>

          <Styled.BodyRow>
            <Styled.Sidebar $scrolled={scrolled}>
              <Styled.MarkerBand />

              {!tasks.length && (
                <>
                  <Styled.WidthSpacer aria-hidden>
                    <Styled.NameCell $width={nameWidth} />
                    {visibleColumns.map((key) => (
                      <Styled.Cell key={key} $width={SIDEBAR_COLUMNS[key].width} $align={SIDEBAR_COLUMNS[key].align} />
                    ))}
                  </Styled.WidthSpacer>
                  <Styled.EmptyMessage>No results</Styled.EmptyMessage>
                </>
              )}

              {model.rows.map(({ task, depth, hasChildren, expanded }) => (
                <Styled.SidebarRow key={task.id} onClick={() => openTask(task)}>
                  <Styled.NameCell $depth={depth} $width={nameWidth}>
                    <Styled.Caret
                      $interactive={hasChildren}
                      onClick={
                        hasChildren
                          ? (e) => {
                              e.stopPropagation();
                              setExpanded(task.id, !expanded);
                            }
                          : undefined
                      }>
                      {hasChildren && <Icon icon={expanded ? 'chevron-down' : 'chevron-right'} />}
                    </Styled.Caret>
                    <TaskCategoryIcon icon={task.taskCategory.icon} color={task.taskCategory.color} />
                    <Styled.TaskName>{task.name}</Styled.TaskName>
                  </Styled.NameCell>
                  {visibleColumns.map((key) => (
                    <Styled.Cell key={key} $width={SIDEBAR_COLUMNS[key].width} $align={SIDEBAR_COLUMNS[key].align}>
                      {SIDEBAR_COLUMNS[key].render(task, project)}
                    </Styled.Cell>
                  ))}
                </Styled.SidebarRow>
              ))}
              <Styled.Resizer onMouseDown={handleResizeDown} onDoubleClick={handleResizeReset} />
            </Styled.Sidebar>

            <Styled.TimelineScroll ref={scrollRef} onScroll={handleTimelineScroll}>
              <svg ref={svgRef} width={model.width} height={bodySvgHeight} onClick={() => setSelectedArrowId(null)}>
                <defs>
                  <marker id="gantt-arrow" markerWidth="8" markerHeight="6" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill={colors.grey55} />
                  </marker>
                  <marker id="gantt-arrow-valid" markerWidth="8" markerHeight="6" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill={colors.primary} />
                  </marker>
                  <marker id="gantt-arrow-invalid" markerWidth="8" markerHeight="6" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill={colors.danger} />
                  </marker>
                  <pattern
                    id="rollup-stripes"
                    patternUnits="userSpaceOnUse"
                    width="8"
                    height="8"
                    patternTransform="rotate(-45)">
                    <rect width="4" height="8" fill={rgba(colors.white, 0.35)} />
                  </pattern>
                </defs>

                {model.minorTicks.map((tick, i) => (
                  <line key={`min-${i}`} x1={tick.x} y1={0} x2={tick.x} y2={bodySvgHeight} stroke={colors.grey5} />
                ))}

                {markers.map((m) => (
                  <MarkerFlag key={m.label} {...m} svgHeight={bodySvgHeight} />
                ))}

                {/* Arrows render first so bars paint over them. */}
                <g transform={`translate(0, ${MARKER_BAND})`}>
                  {project.useDependencies &&
                    model.arrows.map((a) => {
                      // Recompute live only for arrows touching the bar being dragged; others use the cached path.
                      let d = a.d;
                      if (draftBar && (a.predecessorId === draftBar.taskId || a.successorId === draftBar.taskId)) {
                        const from = liveBar(a.predecessorId);
                        const to = liveBar(a.successorId);
                        if (from && to) d = arrowBetween(from, to);
                      }
                      const selected = selectedArrowId === a.id;
                      const canDelete = taskById.get(a.successorId)?.permissions?.manage;
                      return (
                        <g key={a.id}>
                          {canDelete && (
                            <path
                              d={d}
                              fill="none"
                              stroke="transparent"
                              strokeWidth={10}
                              style={{ cursor: 'pointer' }}
                              pointerEvents="stroke"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedArrowId(selected ? null : a.id);
                              }}
                            />
                          )}
                          {!selected && (
                            <path
                              d={d}
                              fill="none"
                              stroke={colors.grey55}
                              strokeWidth={1.25}
                              markerEnd="url(#gantt-arrow)"
                              pointerEvents="none"
                            />
                          )}
                        </g>
                      );
                    })}

                  {model.bars.map((bar) => {
                    const task = taskById.get(bar.taskId);
                    if (!task) return null;
                    const color = STATUS[bar.statusId]?.color ?? colors.grey40;
                    const opacity = bar.isCompleted ? 0.5 : 1;
                    const live = liveBar(bar.taskId);
                    const canMove = !bar.isRollup && task.permissions?.manage;
                    const canResize = !bar.isMilestone && canMove;
                    const canLink = project.useDependencies && !bar.isRollup && task.permissions?.manage;
                    const slimY = bar.y + bar.h / 2 - 4;
                    const cx = live.x + live.w / 2;
                    const cy = bar.y + bar.h / 2;
                    const half = bar.isMilestone ? bar.mHalf : bar.h / 2;
                    return (
                      <Styled.Bar
                        key={bar.taskId}
                        onMouseEnter={(e) => handleBarEnter(e, task)}
                        onMouseLeave={endHover}
                        onClick={() => {
                          if (suppressClickRef.current) {
                            suppressClickRef.current = false;
                            return;
                          }
                          openTask(task);
                        }}>
                        {bar.isMilestone ? (
                          <polygon
                            points={`${cx},${cy - half} ${cx + half},${cy} ${cx},${cy + half} ${cx - half},${cy}`}
                            fill={rgba(color, 0.2)}
                            stroke={color}
                            strokeWidth={1.25}
                            opacity={opacity}
                            style={canMove ? { cursor: 'grab' } : undefined}
                            onMouseDown={canMove ? (e) => handleMoveStart(e, live) : undefined}
                          />
                        ) : bar.isRollup ? (
                          <g opacity={opacity}>
                            <rect
                              x={bar.x}
                              y={slimY}
                              width={bar.w}
                              height={8}
                              rx={2}
                              fill={rgba(color, 0.2)}
                              stroke={color}
                              strokeWidth={1.25}
                            />
                            <rect x={bar.x} y={slimY} width={bar.w} height={8} rx={2} fill="url(#rollup-stripes)" />
                          </g>
                        ) : (
                          <g opacity={opacity}>
                            <rect
                              x={live.x}
                              y={bar.y}
                              width={live.w}
                              height={bar.h}
                              rx={3}
                              fill={rgba(color, 0.2)}
                              stroke={color}
                              strokeWidth={1.25}
                              style={canMove ? { cursor: 'grab' } : undefined}
                              onMouseDown={canMove ? (e) => handleMoveStart(e, live) : undefined}
                            />
                            {canResize && (
                              <>
                                <rect
                                  x={live.x - 3}
                                  y={bar.y}
                                  width={6}
                                  height={bar.h}
                                  fill="transparent"
                                  style={{ cursor: 'ew-resize' }}
                                  onMouseDown={(e) => handleResizeStart(e, live, 'start')}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <rect
                                  x={live.x + live.w - 3}
                                  y={bar.y}
                                  width={6}
                                  height={bar.h}
                                  fill="transparent"
                                  style={{ cursor: 'ew-resize' }}
                                  onMouseDown={(e) => handleResizeStart(e, live, 'end')}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </>
                            )}
                          </g>
                        )}

                        {draftLink?.targetId === bar.taskId &&
                          draftLink.valid &&
                          (bar.isMilestone ? (
                            <polygon
                              points={`${cx},${cy - half - 2} ${cx + half + 2},${cy} ${cx},${cy + half + 2} ${cx - half - 2},${cy}`}
                              fill="none"
                              stroke={colors.primary}
                              strokeWidth={1.5}
                              pointerEvents="none"
                            />
                          ) : (
                            <rect
                              x={live.x - 2}
                              y={bar.y - 2}
                              width={live.w + 4}
                              height={bar.h + 4}
                              rx={4}
                              fill="none"
                              stroke={colors.primary}
                              strokeWidth={1.5}
                              pointerEvents="none"
                            />
                          ))}

                        {canLink && (
                          <g className="gantt-link-handle">
                            <line
                              className="gantt-link-tether"
                              x1={rightAnchor(live)}
                              y1={cy}
                              x2={rightAnchor(live) + PORT_GAP - PORT_R}
                              y2={cy}
                              strokeWidth={1.25}
                              pointerEvents="none"
                            />
                            {/* While dragging a link the hit area is inert, so passing over other dots
                              doesn't hover-highlight them teal — the teal target box + line carry the
                              feedback, and targets are detected geometrically. */}
                            <circle
                              cx={rightAnchor(live) + PORT_GAP}
                              cy={cy}
                              r={PORT_HIT_R}
                              fill="transparent"
                              style={{ cursor: 'crosshair' }}
                              pointerEvents={draftLink ? 'none' : undefined}
                              onMouseDown={(e) => handleLinkStart(e, live)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <circle
                              className="gantt-link-port"
                              cx={rightAnchor(live) + PORT_GAP}
                              cy={cy}
                              r={PORT_R}
                              pointerEvents="none"
                            />
                          </g>
                        )}
                      </Styled.Bar>
                    );
                  })}
                </g>

                {/* Foreground layer: transient interaction UI, drawn above all arrows and bars. */}
                <g transform={`translate(0, ${MARKER_BAND})`}>
                  {draftLinkPath && (
                    <path
                      d={draftLinkPath}
                      fill="none"
                      stroke={draftLinkStroke}
                      strokeWidth={1.5}
                      markerEnd={`url(#${draftLinkMarker})`}
                      pointerEvents="none"
                    />
                  )}

                  {selectedArrowD && (
                    <>
                      <path
                        d={selectedArrowD}
                        fill="none"
                        stroke={colors.primary}
                        strokeWidth={2}
                        pointerEvents="none"
                      />
                      <g
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteLink(selectedArrow);
                        }}>
                        <rect
                          x={deleteBtn.x - 7}
                          y={deleteBtn.y - 7}
                          width={14}
                          height={14}
                          rx={3}
                          fill={colors.primary}
                        />
                        <line
                          x1={deleteBtn.x - 3}
                          y1={deleteBtn.y - 3}
                          x2={deleteBtn.x + 3}
                          y2={deleteBtn.y + 3}
                          stroke={colors.white}
                          strokeWidth={1.5}
                        />
                        <line
                          x1={deleteBtn.x - 3}
                          y1={deleteBtn.y + 3}
                          x2={deleteBtn.x + 3}
                          y2={deleteBtn.y - 3}
                          stroke={colors.white}
                          strokeWidth={1.5}
                        />
                      </g>
                    </>
                  )}
                </g>
              </svg>
            </Styled.TimelineScroll>
          </Styled.BodyRow>
        </Styled.Container>
      </Page.Section>

      {dialog}
      {(dragging || resizing) && <Styled.DragOverlay $cursor={resizing ? 'col-resize' : dragCursor} />}
      {dragging && draftBar && (
        <BarDragTooltip svgRef={svgRef} draftBar={draftBar} bar={barById.get(draftBar.taskId)} anchorX={draftAnchorX} />
      )}
      {hover && <BarHoverTooltip task={hover.task} project={project} referenceElement={hover.ref} left={hover.left} />}
    </>
  );
}
